/**
 * @fileoverview Privacy Policy dialog modal for landing page.
 * @module features/landing/components/PrivacyPolicyDialog
 */

import { ReactElement } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { PrivacyPolicyContent } from './PrivacyPolicyContent'

interface PrivacyPolicyDialogProps {
  /** Trigger element that opens the dialog */
  children: React.ReactNode
}

/**
 * Privacy Policy dialog modal component.
 * Displays GDPR/RODO compliant privacy policy in a scrollable modal.
 *
 * @component
 */
export function PrivacyPolicyDialog({ children }: PrivacyPolicyDialogProps): ReactElement {
  const { t } = useTranslation('features-landing')

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" showCloseButton={false}>
        <DialogHeader className="flex-row items-center justify-between flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {t('privacyPolicy.title')}
          </DialogTitle>
          <DialogClose className="rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2 -mr-2 scrollbar-elegant py-4">
          <PrivacyPolicyContent />
        </div>
      </DialogContent>
    </Dialog>
  )
}
